/**
 * "Saved to memory · Undo" lines — shared by the three AI doors (editor panel,
 * Tender Chat, Assistant). The agent's `remember` tool emits `memory_saved`
 * { memoryId, bidId, content }; each store records a line on the turn's reply
 * and Undo deletes the item. The helpers are generic over the store's message
 * type so every door gets identical parsing and Undo semantics.
 */

import { deleteBidMemoryItem } from '@/lib/api'

/**
 * Lifecycle of a "Saved to memory · Undo" line:
 * saved → undoing → undone | already_removed; a failed Undo lands on
 * undo_failed, which can be retried. `already_removed` means someone else
 * removed the item first (or it is no longer on the bid): show it quietly,
 * it is not an error.
 */
export type MemorySavedStatus = 'saved' | 'undoing' | 'undone' | 'already_removed' | 'undo_failed'

/** One bid-memory item the agent saved during a reply (`memory_saved`). */
export interface MemorySavedLine {
  memoryId: string
  /** The bid the item belongs to: Undo is `DELETE /bids/{bidId}/memory/{memoryId}`. */
  bidId: string
  content: string
  status: MemorySavedStatus
}

/** Any store message that can carry saved-memory lines. */
export interface MemorySavedCarrier {
  id: string
  memorySaved?: MemorySavedLine[]
}

/** Parse a `memory_saved` event payload; null when it is malformed. */
export function parseMemorySavedLine(data: Record<string, unknown>): MemorySavedLine | null {
  const memoryId = data['memoryId']
  const bidId = data['bidId']
  const content = data['content']
  if (typeof memoryId !== 'string' || memoryId.length === 0) return null
  if (typeof bidId !== 'string' || bidId.length === 0) return null
  if (typeof content !== 'string') return null
  return { memoryId, bidId, content, status: 'saved' }
}

/** True when the message carries at least one saved-memory line. */
export function hasMemorySavedLines(message: MemorySavedCarrier | null | undefined): boolean {
  return Boolean(message?.memorySaved && message.memorySaved.length > 0)
}

/** Append `line` to message `targetId`. A replayed event (same memoryId) is ignored. */
export function appendMemorySavedLine<M extends MemorySavedCarrier>(
  messages: M[],
  targetId: string,
  line: MemorySavedLine
): M[] {
  return messages.map((m) => {
    if (m.id !== targetId) return m
    const existing = m.memorySaved ?? []
    if (existing.some((l) => l.memoryId === line.memoryId)) return m
    return { ...m, memorySaved: [...existing, line] }
  })
}

export function updateMemoryLine<M extends MemorySavedCarrier>(
  messages: M[],
  messageId: string,
  memoryId: string,
  status: MemorySavedStatus
): M[] {
  return messages.map((m) =>
    m.id === messageId && m.memorySaved
      ? { ...m, memorySaved: m.memorySaved.map((line) => (line.memoryId === memoryId ? { ...line, status } : line)) }
      : m
  )
}

/**
 * Undo one line: deletes the item and records the outcome on the line. Never
 * throws. A press while one is in flight, or on a line already undone, does
 * nothing. `already_deleted` / `not_found` land on `already_removed`.
 */
export async function undoMemorySavedLine<M extends MemorySavedCarrier>(
  getMessages: () => M[],
  setMessages: (update: (messages: M[]) => M[]) => void,
  messageId: string,
  memoryId: string
): Promise<void> {
  const line = getMessages()
    .find((m) => m.id === messageId)
    ?.memorySaved?.find((l) => l.memoryId === memoryId)
  if (!line || (line.status !== 'saved' && line.status !== 'undo_failed')) return

  setMessages((messages) => updateMemoryLine(messages, messageId, memoryId, 'undoing'))
  let next: MemorySavedStatus
  try {
    const { status } = await deleteBidMemoryItem(line.bidId, memoryId)
    next = status === 'deleted' ? 'undone' : 'already_removed'
  } catch {
    next = 'undo_failed'
  }
  setMessages((messages) => updateMemoryLine(messages, messageId, memoryId, next))
}

/**
 * The reply placeholder when a turn fails or is stopped: dropped if it carries
 * no saved-memory line, otherwise kept (no longer streaming) so its Undo stays
 * reachable. The item was saved even though the turn did not finish.
 */
export function dropOrSettleReply<M extends MemorySavedCarrier & { isStreaming?: boolean }>(
  messages: M[],
  shouldRelease: (message: M) => boolean
): M[] {
  const out: M[] = []
  for (const m of messages) {
    if (!shouldRelease(m)) {
      out.push(m)
    } else if (hasMemorySavedLines(m)) {
      out.push({ ...m, isStreaming: false })
    }
  }
  return out
}
